// src/sections/EscenariosSection.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import API_BASE_URL from "../config/config.js";
import ModalSede from "../components/Modals/ModalSede/ModalSede.jsx";
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiExternalLink, FiX } from "react-icons/fi";

/** --------- Helpers robustos --------- */
const ensureArray = (x) => (Array.isArray(x) ? x : []);
const safeText = (v, fallback = "") => (typeof v === "string" ? v : fallback);
const isPositiveInt = (n) => Number.isInteger(n) && n > 0;
const toPosIntOrNull = (v) => {
    const n = Number(v);
    return isPositiveInt(n) ? n : null;
};
const isValidURL = (str) => {
    try {
        const u = new URL(str);
        return u.protocol === "http:" || u.protocol === "https:";
    } catch {
        return false;
    }
};
const normalizeDepartamento = (raw) => ({
    iddepartamento: toPosIntOrNull(raw?.iddepartamento),
    name: safeText(raw?.name, "Sin nombre"),
});
const normalizeSede = (raw) => ({
    idsede: toPosIntOrNull(raw?.idsede),
    name: safeText(raw?.name, ""),
    address: safeText(raw?.address, ""),
    linkMap: safeText(raw?.linkMap, ""),
    iddepartamento: toPosIntOrNull(raw?.iddepartamento),
    state: typeof raw?.state === "number" ? raw.state : 1,
});
const compareByName = (a, b) =>
    safeText(a?.name).localeCompare(safeText(b?.name), "es", { sensitivity: "base" });

// Normaliza string para búsqueda acento/espacio-insensible
const normStr = (s) =>
    safeText(s)
        .normalize("NFD")
        .replace(/\p{Diacritic}+/gu, "")
        .toLowerCase()
        .trim();

/** ------------------------------------ */

const EscenariosSection = () => {
    const [departamentos, setDepartamentos] = useState([]);
    const [selectedDepartamento, setSelectedDepartamento] = useState(1); // Lima por defecto
    const [sedes, setSedes] = useState([]);
    const [loadingDeps, setLoadingDeps] = useState(true);
    const [loadingSedes, setLoadingSedes] = useState(false);
    const [error, setError] = useState("");
    const [hasSearched, setHasSearched] = useState(false);

    // Filtro
    const [filterText, setFilterText] = useState("");
    const [debouncedFilter, setDebouncedFilter] = useState("");

    // Modal
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
    const [modalInitial, setModalInitial] = useState({
        idsede: null,
        name: "",
        address: "",
        linkMap: "",
        iddepartamento: 1,
    });

    // Debounce del filtro (250ms)
    useEffect(() => {
        const t = setTimeout(() => setDebouncedFilter(filterText), 250);
        return () => clearTimeout(t);
    }, [filterText]);

    // Traer departamentos
    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoadingDeps(true);
            setError("");
            try {
                const resp = await axios.get(`${API_BASE_URL}/departamento`);
                if (!mounted) return;

                const arr = ensureArray(resp?.data)
                    .map(normalizeDepartamento)
                    .filter((d) => d.iddepartamento !== null);

                const sorted = [...arr].sort(compareByName);
                setDepartamentos(sorted);

                // Si Lima (1) no existe, usa el primero válido
                if (!sorted.some((d) => d.iddepartamento === 1)) {
                    const fallbackId = sorted[0]?.iddepartamento;
                    if (fallbackId) setSelectedDepartamento(fallbackId);
                }
            } catch (e) {
                if (mounted) setError("Error al cargar departamentos.");
                console.error(e);
            } finally {
                if (mounted) setLoadingDeps(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    const selectedDepName = useMemo(() => {
        const d = departamentos.find((x) => x.iddepartamento === selectedDepartamento);
        return d?.name ?? "Departamento";
    }, [departamentos, selectedDepartamento]);

    // Buscar sedes por departamento
    const handleSearch = useCallback(async () => {
        setLoadingSedes(true);
        setError("");
        setHasSearched(true);
        setFilterText(""); // resetea filtro al buscar
        try {
            const resp = await axios.post(`${API_BASE_URL}/getsedes`, {
                iddepartamento: selectedDepartamento,
            });

            const arr = ensureArray(resp?.data)
                .map(normalizeSede)
                // estado activo + coincide con dpto + idsede válido
                .filter(
                    (s) =>
                        s.state !== 0 &&
                        s.iddepartamento === selectedDepartamento &&
                        s.idsede !== null
                );

            // Dedupe por idsede
            const dedupMap = {};
            for (const s of arr) {
                if (!(s.idsede in dedupMap)) dedupMap[s.idsede] = s;
            }
            const dedup = Object.values(dedupMap).sort(compareByName);
            setSedes(dedup);
        } catch (e) {
            setError("Error al cargar sedes.");
            console.error(e);
        } finally {
            setLoadingSedes(false);
        }
    }, [selectedDepartamento]);

    // Abrir modal crear
    const handleOpenCreate = () => {
        setModalMode("create");
        setModalInitial({
            idsede: null,
            name: "",
            address: "",
            linkMap: "",
            iddepartamento: selectedDepartamento,
        });
        setModalOpen(true);
    };

    // Abrir modal editar (defensivo)
    const handleOpenEdit = (sede) => {
        const s = normalizeSede(sede);
        if (s.idsede === null) {
            alert("No se puede editar: sede inválida.");
            return;
        }
        setModalMode("edit");
        setModalInitial({
            idsede: s.idsede,
            name: s.name,
            address: s.address,
            linkMap: s.linkMap,
            iddepartamento: s.iddepartamento ?? selectedDepartamento,
        });
        setModalOpen(true);
    };

    // Eliminar lógico (defensivo)
    const handleDelete = useCallback(async (sede) => {
        const s = normalizeSede(sede);
        if (s.idsede === null) {
            alert("No se puede eliminar: sede inválida.");
            return;
        }
        const ok = window.confirm(`¿Eliminar lógicamente la sede "${s.name || "Sin nombre"}"?`);
        if (!ok) return;
        try {
            await axios.put(`${API_BASE_URL}/updatesede/${s.idsede}`, { state: 0 });
            setSedes((prev) => prev.filter((x) => x.idsede !== s.idsede));
        } catch (e) {
            console.error(e);
            alert("No se pudo eliminar la sede.");
        }
    }, []);

    // Al guardar (crear/editar) desde el modal (defensivo + normalización)
    const handleSaved = useCallback(
        (savedRaw) => {
            const saved = Array.isArray(savedRaw) ? savedRaw[0] : savedRaw; // por si alguna vez llega array
            const s = normalizeSede(saved);
            if (s.idsede === null) return;

            setSedes((prev) => {
                const idx = prev.findIndex((x) => x.idsede === s.idsede);

                // Si la volvieron inactiva, se remueve
                if (s.state === 0) {
                    return prev.filter((x) => x.idsede !== s.idsede);
                }

                // Si pertenece a otro dpto, la quitamos (si estaba) o no la agregamos
                if (s.iddepartamento !== selectedDepartamento) {
                    if (idx !== -1) {
                        const next = [...prev];
                        next.splice(idx, 1);
                        return next;
                    }
                    return prev;
                }

                if (idx === -1) {
                    return [...prev, s].sort(compareByName);
                }
                const next = [...prev];
                next[idx] = { ...next[idx], ...s };
                return next.sort(compareByName);
            });
        },
        [selectedDepartamento]
    );

    // Lista filtrada por nombre o dirección (filtro local)
    const filteredSedes = useMemo(() => {
        const q = normStr(debouncedFilter);
        if (!q) return sedes;

        return sedes.filter((s) => {
            const n = normStr(s?.name);
            const a = normStr(s?.address);
            return n.includes(q) || a.includes(q);
        });
    }, [sedes, debouncedFilter]);

    return (
        <>
            <div className="p-6 bg-gray-100 min-h-screen">
                <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">
                    Escenarios por Departamento
                </h1>

                {/* WRAPPER de ancho fijo para mantener consistencia SIEMPRE */}
                <div className="w-full max-w-5xl mx-auto">
                    {/* Barra superior (select + acciones) */}
                    <div className="bg-white rounded-2xl shadow p-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        {/* Select */}
                        <div className="flex-1 min-w-[240px]">
                            <label htmlFor="dep" className="block text-sm text-gray-600 mb-1">
                                Departamento
                            </label>
                            <select
                                id="dep"
                                className="w-full border rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                                value={selectedDepartamento}
                                onChange={(e) => {
                                    const value = Number(e.target.value);
                                    setSelectedDepartamento(isPositiveInt(value) ? value : selectedDepartamento);
                                }}
                                disabled={loadingDeps}
                            >
                                {loadingDeps ? (
                                    <option>Cargando...</option>
                                ) : departamentos.length > 0 ? (
                                    departamentos.map((dep) => (
                                        <option key={dep.iddepartamento} value={dep.iddepartamento}>
                                            {safeText(dep.name, "Sin nombre")}
                                        </option>
                                    ))
                                ) : (
                                    <option value={selectedDepartamento}>Sin departamentos</option>
                                )}
                            </select>
                        </div>

                        {/* Acciones */}
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={handleSearch}
                                disabled={loadingDeps || loadingSedes}
                                title={`Buscar sedes en ${selectedDepName}`}
                                className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-semibold px-4 py-2 rounded-xl shadow transition disabled:opacity-60"
                            >
                                <FiSearch className="text-lg" />
                                Buscar
                            </button>

                            <button
                                type="button"
                                onClick={handleOpenCreate}
                                disabled={loadingDeps}
                                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold px-4 py-2 rounded-xl shadow transition disabled:opacity-60"
                            >
                                <FiPlus className="text-lg" />
                                Nueva sede
                            </button>
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3">
                            {safeText(error, "Ocurrió un error.")}
                        </div>
                    )}

                    {/* Contenido resultados (mismo contenedor ancho) */}
                    <div className="mt-6">
                        {loadingSedes && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <div key={i} className="bg-white rounded-2xl shadow p-4 animate-pulse">
                                        <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" />
                                        <div className="h-4 bg-gray-200 rounded w-5/6 mb-2" />
                                        <div className="h-4 bg-gray-200 rounded w-2/3" />
                                    </div>
                                ))}
                            </div>
                        )}

                        {!loadingSedes && hasSearched && sedes.length === 0 && (
                            <div className="text-center text-gray-600 bg-white rounded-2xl shadow p-10">
                                No hay sedes activas en <b>{selectedDepName}</b>.
                            </div>
                        )}

                        {!loadingSedes && sedes.length > 0 && (
                            <>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                                    <h2 className="text-xl font-semibold text-gray-700">
                                        {`Sedes en ${selectedDepName}`}
                                    </h2>

                                    {/* Input de filtro */}
                                    <div className="relative w-full sm:w-80">
                                        <input
                                            type="text"
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            placeholder="Filtrar por nombre o dirección..."
                                            className="w-full border rounded-lg px-10 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                                        {filterText && (
                                            <button
                                                type="button"
                                                onClick={() => setFilterText("")}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                                title="Limpiar"
                                            >
                                                <FiX />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Conteo */}
                                <div className="text-sm text-gray-500 mb-2">
                                    {filteredSedes.length} / {sedes.length} sedes
                                </div>

                                {/* Grid de sedes filtradas */}
                                {filteredSedes.length === 0 ? (
                                    <div className="text-center text-gray-600 bg-white rounded-2xl shadow p-10">
                                        No hay coincidencias para <b>{debouncedFilter}</b>.
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {filteredSedes.map((sede, idx) => {
                                            const s = normalizeSede(sede);
                                            if (s.idsede === null) return null; // skip inválidos

                                            return (
                                                <div key={s.idsede ?? `tmp-${idx}`} className="bg-white shadow-lg rounded-2xl p-4 transition hover:shadow-xl">
                                                    <div className="flex items-start justify-between gap-3 mb-2">
                                                        <h3 className="text-lg font-bold text-gray-800 break-words">
                                                            {safeText(s.name, "Sin nombre")}
                                                        </h3>

                                                        <div className="flex items-center gap-2 shrink-0">
                                                            <button
                                                                onClick={() => handleOpenEdit(s)}
                                                                className="p-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700"
                                                                title="Editar sede"
                                                            >
                                                                <FiEdit2 />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(s)}
                                                                className="p-2 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700"
                                                                title="Eliminar (lógico)"
                                                            >
                                                                <FiTrash2 />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <p className="text-gray-600 text-sm mb-1">
                                                        <span className="font-semibold">Dirección:</span>{" "}
                                                        {safeText(s.address, "No disponible") || "No disponible"}
                                                    </p>

                                                    {isValidURL(s.linkMap) && (
                                                        <a
                                                            href={s.linkMap}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-indigo-600 hover:underline text-sm inline-flex items-center gap-1 mt-2"
                                                        >
                                                            Ver en Google Maps <FiExternalLink />
                                                        </a>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal (crear/editar) */}
            {modalOpen && (
                <ModalSede
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    mode={modalMode}
                    initial={modalInitial}
                    onSaved={handleSaved}
                />
            )}
        </>
    );
};

export default EscenariosSection;
